import { useCallback, useEffect, useSyncExternalStore, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import { useNavigate as useRouterNavigate, useParams, useSearchParams, type NavigateFunction } from "react-router";
import { getRouter } from "./router";
import type { Navigate } from "@omnabase/ui";

/** Ported from the prototype's lib/nav.ts. The app lives at the root of app.omnabase.com, so there is no base path; every
 *  path inside a workspace starts with its slug (DECISIONS 2026-09-24), which `useWs()` prepends. */

/** The phone moves between levels like a native app: deeper slides in from the right, back slides out to it; a tab is no movement. */
export type Slide = "forward" | "back" | "up" | "none";
export const onPhone = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
let ownBack = false;
export function slide(direction: Slide) { document.documentElement.dataset.slide = direction; if (direction === "back") ownBack = true; }
/** the options a navigation on a phone takes to be animated; on wider screens nothing moves */
export const moving = (direction: Slide = "forward") => { if (!onPhone()) return {}; slide(direction); return { viewTransition: direction !== "none" }; };
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => { if (ownBack) ownBack = false; else document.documentElement.dataset.slide = "none"; });
}

/** React Router's useNavigate, except that a navigation with `viewTransition` (the phone's slides) goes to the router instance:
 *  inside the app's own <Routes> the hook would drop the option (see lib/router.ts). Every screen imports this one. */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  return useCallback(((to, opts) => {
    if (typeof to === "number") return navigate(to);
    const r = getRouter();
    if (opts?.viewTransition && r) return r.navigate(to, opts);
    return navigate(to, opts);
  }) as NavigateFunction, [navigate]);
}

/** Turns the ui package's plain <a href> links into client-side navigation. */
export function useNav(): Navigate {
  const navigate = useNavigate();
  return (to: string, e: MouseEvent<HTMLAnchorElement>) => {
    if (to === "#") { e.preventDefault(); return; }
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to, moving("forward"));
  };
}

/** The workspace slug from the URL and a way to build paths inside it: ws("/inbox") → "/kriesi/inbox". */
export function useWs(): { slug: string; ws: (path: string) => string } {
  const { workspace = "" } = useParams();
  return { slug: workspace, ws: (path: string) => `/${workspace}${path === "/" ? "" : path}` };
}

/** The search params of the URL as the browser has it right now: React Router's copy is the last render's, and two changes
 *  before a re-render would undo each other (the state-in-the-URL rule). */
export const currentParams = () => new URLSearchParams(window.location.search);

/** Sets or clears one search param, replacing the history entry, starting from the URL as it is now. */
export function useSetParam(): (key: string, value: string | null, /** drawn at once (2026-10-12): a dialog a click opens is there when the click returns */ now?: boolean) => void {
  const [, setParams] = useSearchParams();
  return useCallback((key: string, value: string | null, now?: boolean) => {
    if (now) flushSync(() => setAhead(key, value)); else if (ahead.has(key)) setAhead(key, value);
    const n = currentParams(); if (value) n.set(key, value); else n.delete(key); setParams(n, { replace: true });
  }, [setParams]);
}

/* A param set with `now` is known to the screen before the router has drawn the new URL (a navigation is a transition and may wait behind
 * another one): `useParamNow` answers it at once, and forgets it once the URL says the same. One management screen (2026-10-12): its
 * dialogs open on the click itself. */
const ahead = new Map<string, string | null>();
const listeners = new Set<() => void>();
function setAhead(key: string, value: string | null) { ahead.set(key, value); for (const l of listeners) l(); }
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
/** one search param, including a value just set with `now` that the URL does not show yet */
export function useParamNow(key: string): string | null {
  const [params] = useSearchParams();
  const fromUrl = params.get(key);
  const early = useSyncExternalStore(subscribe, () => (ahead.has(key) ? ahead.get(key)! : undefined));
  useEffect(() => { if (ahead.has(key) && ahead.get(key) === fromUrl) ahead.delete(key); }, [key, fromUrl]);
  return early !== undefined ? early : fromUrl;
}
